import { useMsal } from '@azure/msal-react';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Autocomplete, Box, Chip, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import React, { useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Role } from '@/contexts/Settings.types';
import { useLogout } from '@/features/identity/identity.services';
import { setImpersonatedUserId } from '@/helpers/readOnlyImpersonation';
import { getGreetingMessage } from '@/helpers/stringUtils';
import SettingsIcon from '@/icons/SettingsIcon';
import { useGlobalAdminMenuItems } from '@/layouts/layout.constants';
import { UserMenuItem } from '@/layouts/layout.types';
import Notification from '@/layouts/navbar/Notification';
import ProfileAvatar from '@/layouts/navbar/ProfileAvatar';
import WeavrLogo from '@/public/svgs/new_logo.svg';
import useGlobalSettingsActions from '@/store/useGlobalSettingsActions';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';

const SocialFeedTopNav = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const closeMenusTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { globalAdminMenuItems } = useGlobalAdminMenuItems();

  const { isAuthenticated, globalSettings } = useGlobalSettingsStore(
    useShallow(state => ({
      globalSettings: state.globalSettings,
      isAuthenticated: state.isAuthenticated
    }))
  );

  const { updateSettings } = useGlobalSettingsActions();
  const { instance, accounts } = useMsal();

  const logoutMutation = useLogout();

  const handleOpenUserMenu = () => {
    if (closeMenusTimerRef.current !== null) {
      window.clearTimeout(closeMenusTimerRef.current);
      closeMenusTimerRef.current = null;
    }
    setIsAdminMenuOpen(false);
    setIsUserMenuOpen(true);
  };

  const clearMenusCloseTimer = () => {
    if (closeMenusTimerRef.current !== null) {
      window.clearTimeout(closeMenusTimerRef.current);
      closeMenusTimerRef.current = null;
    }
  };

  const scheduleMenusClose = () => {
    clearMenusCloseTimer();
    closeMenusTimerRef.current = window.setTimeout(() => {
      setIsAdminMenuOpen(false);
      setIsUserMenuOpen(false);
    }, 100);
  };

  const handleLogoutRedirect = async () => {
    const logoutRequest = {
      postLogoutRedirectUri: '/'
    };
    if (accounts.length > 0) {
      await instance.logoutRedirect(logoutRequest);
    } else {
      const result = await logoutMutation.mutateAsync({});
      if (result) {
        window.location.href = '/';
      }
    }
  };

  const handleCloseUserMenu = (setting: unknown) => {
    setIsAdminMenuOpen(false);
    setIsUserMenuOpen(false);
    if (typeof setting !== 'string') return;
    switch (setting?.toLowerCase()) {
      case 'logout':
        handleLogoutRedirect();
        break;
      case 'profile':
        navigate({ to: '/profile' });
        break;
      default:
        console.warn(`Unknown setting: ${setting}`);
    }
  };

  const handleCloseMenus = () => {
    setIsAdminMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const userMenuItems: UserMenuItem[] = [
    {
      label: 'Review',
      path: '/updates/review',
      isVisible:
        globalSettings.isStrategyOwner ||
        globalSettings.role === Role.GlobalAdmin ||
        globalSettings.enabledFeatures.includes('DeliverableWorkflows'),
      icon: FactCheckOutlinedIcon
    },
    {
      label: 'Admin',
      isVisible: globalSettings.role === Role.GlobalAdmin,
      icon: AdminPanelSettingsOutlinedIcon,
      isAdminTrigger: true
    },
    { label: 'Settings', path: '/admin/subscription', icon: SettingsIcon },
    { label: 'Profile', path: '/profile', icon: PersonOutlineOutlinedIcon, isDividerBefore: true },
    { label: 'Logout', icon: LogoutOutlinedIcon }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0,
        bgcolor: 'background.paper',
        height: '56px'
      }}
    >
      <Link to='/'>
        <Box
          sx={{
            width: 64,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer'
          }}
        >
          <img src={WeavrLogo} alt='Weavr Logo' width={32} height={32} />
        </Box>
      </Link>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pr: 2,
          flex: 1,
          height: '100%'
        }}
      >
        {globalSettings?.organisationName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {globalSettings.organisationLogoUrl && (
              <img
                src={globalSettings.organisationLogoUrl}
                alt={globalSettings.organisationName}
                width={32}
                height={32}
                className='rounded-lg border border-[#E5E5E5] object-contain'
              />
            )}
            <Box
              sx={{
                px: 0.25,
                py: 0.5,
                color: '#0A0A0A',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {globalSettings.organisationName}
            </Box>
          </Box>
        )}

        <div className='flex-1' />

        {globalSettings?.allOrganisations && globalSettings?.allOrganisations.length > 0 ? (
          <>
            {globalSettings?.canImpersonateUsers && (
              <Autocomplete
                size='small'
                disableClearable
                options={[
                  { id: '', display: 'Global Admin', role: 'Full access' },
                  ...(globalSettings.impersonationUsers || [])
                ]}
                value={
                  [{ id: '', display: 'Global Admin', role: 'Full access' }, ...(globalSettings.impersonationUsers || [])]
                    .find(option => option.id === (globalSettings.impersonatedUserId || '')) ||
                  { id: '', display: 'Global Admin', role: 'Full access' }
                }
                getOptionLabel={option => option.display}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={async (_, option) => {
                  setImpersonatedUserId(option.id || null);
                  await queryClient.invalidateQueries();
                  navigate({ to: '/updates' });
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || 'global-admin'}>
                    <Box>
                      <Typography variant='body2'>{option.display}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {option.role || 'Member'}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={params => <TextField {...params} label='User' />}
                sx={{ width: 220 }}
              />
            )}
            {globalSettings.isReadOnlyImpersonation && <Chip size='small' label='Read only' color='warning' />}
            <FormControl size='small' sx={{ minWidth: 150 }}>
              <InputLabel>Subscription</InputLabel>
              <Select
                label='Subscription'
                value={globalSettings?.organisationName}
                onChange={async e => {
                  setImpersonatedUserId(null);
                  const newSettings = {
                    organisationName: e.target.value
                  };
                  await updateSettings(newSettings);
                }}
              >
                {globalSettings.allOrganisations?.map(g => (
                  <MenuItem value={g} key={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <div className='text-pretty font-bold'>{`${getGreetingMessage(globalSettings?.firstName || '')}`}</div>
        )}

        {isAuthenticated && (
          <div data-tour='notifications'>
            <Notification isSocialMode={true} />
          </div>
        )}

        <Box sx={{ position: 'relative' }} onMouseEnter={handleOpenUserMenu} onMouseLeave={scheduleMenusClose}>
          <Tooltip title='Open settings'>
            <IconButton sx={{ p: 0 }} data-tour='profile'>
              <ProfileAvatar alt='profile picture' />
            </IconButton>
          </Tooltip>

          {isUserMenuOpen ? (
            <Box
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: 240,
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                border: '1px solid',
                borderColor: 'divider',
                zIndex: 1300
              }}
            >
              {userMenuItems
                .filter(item => item.isVisible !== false)
                .map(item => (
                  <MenuItem
                    key={item.label}
                    onClick={() => {
                      if (item.isAdminTrigger) {
                        return;
                      }

                      if (item.path) {
                        handleCloseMenus();
                        navigate({ to: item.path });
                        return;
                      }

                      handleCloseUserMenu(item.label);
                    }}
                    sx={{
                      position: 'relative',
                      borderTop: item.isDividerBefore ? '1px solid #E5E7EB' : undefined
                    }}
                    onMouseEnter={() => {
                      clearMenusCloseTimer();
                      setIsAdminMenuOpen(Boolean(item.isAdminTrigger));
                    }}
                  >
                    <div className='flex items-center gap-2'>
                      {item.icon ? <item.icon className='h-[18px] w-[18px]' /> : undefined}
                      <Typography sx={{ textAlign: 'center' }}>{item.label}</Typography>
                    </div>

                    {item.isAdminTrigger && isAdminMenuOpen ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: '100%',
                          minWidth: 240,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        {globalAdminMenuItems.map(adminItem => (
                          <Box
                            component='div'
                            key={adminItem.url}
                            onClick={() => {
                              handleCloseMenus();
                              navigate({ to: adminItem.url });
                            }}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <div className='flex flex-col gap-1'>
                              <span className='text-sm font-medium leading-none'>{adminItem.name}</span>
                              <span className='line-clamp-2 text-xs leading-snug text-[#6B7280]'>
                                {adminItem.description}
                              </span>
                            </div>
                          </Box>
                        ))}
                      </Box>
                    ) : undefined}
                  </MenuItem>
                ))}
            </Box>
          ) : undefined}
        </Box>
      </Box>
    </Box>
  );
};

export default SocialFeedTopNav;
